import {
  Box,
  Card,
  CardContent,
  Chip,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useState } from "react";

import { useMigrationUploads } from "../../hooks/useMigrationUploads";
import { downloadErrorsFile } from "@/shared/actions/campanas/migracion.actions";

type CampaignType = "MIGRATION" | "FIDELIZATION";

interface TableMigracionProps {
  campaignType: CampaignType;
}

const parseCsvLine = (line: string) => {
  const result: string[] = [];
  let current = "";
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && nextChar === '"') {
      current += '"';
      i++;
    } else if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if ((char === "," || char === ";") && !insideQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

export const TableMigracion = ({ campaignType }: TableMigracionProps) => {
  const [page, setPage] = useState(1);
  const size = 2;

  const { data, loading } = useMigrationUploads(page - 1, size, campaignType);

  const rows = (data as any)?.content ?? [];
  const totalPages = (data as any)?.page?.totalPages ?? 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SUCCESS":
        return "success";
      case "FAILED":
        return "error";
      case "WITH_ERRORS":
        return "warning";
      default:
        return "default";
    }
  };

  const handleDownload = async (id: number, fileName: string) => {
    try {
      const response = await downloadErrorsFile(id);

      let csvText = "";

      if (response.data instanceof Blob) {
        csvText = await response.data.text();
      } else if (typeof response.data === "string") {
        csvText = response.data;
      } else {
        csvText = new TextDecoder("utf-8").decode(response.data);
      }

      const excelRows = csvText
        .replace(/^\uFEFF/, "")
        .split(/\r?\n/)
        .filter((line) => line.trim() !== "")
        .map((line) => parseCsvLine(line));

      const worksheet = XLSX.utils.aoa_to_sheet(excelRows);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Errores");

      const cleanFileName = fileName.replace(/\.[^/.]+$/, "");

      XLSX.writeFile(workbook, `errores_${cleanFileName}.xlsx`);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
      <CardContent>
        <Typography variant="h5" fontWeight={700} mb={2}>
          Historial de cargas y trazabilidad -{" "}
          {campaignType === "MIGRATION" ? "Migración" : "Fidelización"}
        </Typography>

        {loading ? (
          <Typography>Cargando...</Typography>
        ) : (
          <Stack spacing={3}>
            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Archivo</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Módulo</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Fecha</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Exitosos</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Errores</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Estado</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row: any) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {row.fileName}

                          {row.errorRecords > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(row.id, row.fileName)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>{row.module}</TableCell>

                      <TableCell>
                        {new Date(row.createdAt).toLocaleDateString("es-EC")}
                      </TableCell>

                      <TableCell>{row.totalRecords}</TableCell>
                      <TableCell>{row.successRecords}</TableCell>
                      <TableCell>{row.errorRecords}</TableCell>

                      <TableCell>
                        <Chip
                          label={row.status}
                          color={getStatusColor(row.status) as any}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}>No hay cargas registradas</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1}
            >
              {totalPages > 1 && (
                <Pagination
                  page={page}
                  count={totalPages}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                />
              )}

              <Typography variant="body2" color="text.secondary">
                Total de registros: {(data as any)?.page?.totalElements ?? 0}
              </Typography>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};